import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Custom validators for sanitario forms
 */
export class SanitarioValidators {
  /**
   * Validates that the control value is not only whitespace
   */
  static noWhitespace(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    const isWhitespace = value.toString().trim().length === 0;
    const isEmpty = !value;

    // Don't show whitespace error if field is empty (required validator handles this)
    if (isEmpty) return null;

    return !isWhitespace ? null : { whitespace: true };
  }

  /**
   * Validates that the control value matches the Italian codice fiscale format.
   */
  static codiceFiscale(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';
    if (!value) return null;
    const pattern = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/;
    return pattern.test(value.toUpperCase())
      ? null
      : { invalidCodiceFiscale: { message: 'Codice fiscale non valido' } };
  }

  /**
   * Validates a phone number: optional leading "+", then 6 to 15 digits (spaces allowed).
   */
  static telefono(control: AbstractControl): ValidationErrors | null {
    const value: string = control.value || '';
    if (!value) return null;
    const pattern = /^\+?[0-9]{6,15}$/;
    return pattern.test(value.replace(/\s/g, ''))
      ? null
      : { pattern: { message: 'Numero di telefono non valido' } };
  }

  /**
   * Creates an async validator that checks if a codice fiscale is valid via API
   * @param validateFn - Function that calls the validation API
   * @returns AsyncValidatorFn
   */
  static codiceFiscaleAsync(
    validateFn: (codiceFiscale: string) => Observable<unknown>
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;

      // Only validate when exactly 16 characters are entered
      if (!value || value.length !== 16) {
        return of(null);
      }

      return validateFn(value).pipe(
        map(() => {
          // If API call succeeds, no validation error
          return null;
        }),
        catchError(() => {
          // If API call fails, return validation error
          return of({
            invalidCodiceFiscale: { message: 'Codice fiscale non valido' },
          });
        })
      );
    };
  }
}
